import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../components/Box";
import { Helpers } from "../../../helpers/Helpers";
import { KTCard } from "../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../_metronic/layout/core";
import { storeActivityIndicator } from "../../../redux/reducers/authSlice";
import { RootState } from "../../../redux/store";
import CustomSwitch from "../../../components/CustomSwitch";
import MailListesiAlani from "../../../components/MailListesiAlani";
import { MUTABAKAT_AYAR_SAYFASI, MUTABAKAT_SAYFASI, MutabakatAyari, YurticiMutabakatService } from "../../../services/YurticiMutabakatService";

const GUNLER = [
    { value: 1, label: 'Pazartesi' }, { value: 2, label: 'Salı' }, { value: 3, label: 'Çarşamba' }, { value: 4, label: 'Perşembe' },
    { value: 5, label: 'Cuma' }, { value: 6, label: 'Cumartesi' }, { value: 7, label: 'Pazar' },
];

const saatGecerli = (s: string) => /^([01]?\d|2[0-3]):[0-5]\d$/.test(s.trim());

/**
 * Ayarlar > Yurtiçi Gider Mutabakatı (24.10.2026, Burak): mail kutusuna hangi gün ve saatlerde bakılacağı (Yurtiçi faturayı
 * çarşamba kesiyor; rapor maili sonraki günlerde gelebiliyor) ve fark / hata maillerinin alıcıları (Kime + Bilgi / CC,
 * virgülle birden çok). Seçilen zamanlar dışında sistem mail kutusuna gitmez. Firma başına tek ayar.
 */
const YurticiMutabakatAyar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector((state: RootState) => state.auth);
    const pageName = MUTABAKAT_AYAR_SAYFASI;
    const guncelleyebilir = !!auth.user.user?.data.authorizedMenus.find(x => x.menuName == pageName)?.canUpdate;
    const mutabakatYetkisi = !!auth.user.user?.data.authorizedMenus.find(x => x.menuName == MUTABAKAT_SAYFASI);

    const [ayar, setAyar] = useState<MutabakatAyari | undefined>(undefined);
    const [aktif, setAktif] = useState<boolean>(false);
    const [gunler, setGunler] = useState<number[]>([]);
    const [saatler, setSaatler] = useState<string[]>([]);
    const [yeniSaat, setYeniSaat] = useState<string>('10:00');
    const [kime, setKime] = useState<string | undefined>(undefined);
    const [bilgi, setBilgi] = useState<string | undefined>(undefined);
    const [sonrakiler, setSonrakiler] = useState<string[]>([]);

    useEffect(() => {
        getir();
    }, [])

    const yukle = (a: MutabakatAyari) => {
        setAyar(a);
        setAktif(a.aktif);
        setGunler(a.gunler);
        setSaatler(a.saatler);
        setKime(a.kime ?? undefined);
        setBilgi(a.bilgi ?? undefined);
        setSonrakiler(a.sonrakiler);
    }

    const getir = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await YurticiMutabakatService.ayarGetir();

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            yukle(response.data);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    // Gün / saat değişince sonraki 5 çalışma (kaydetmeden) yenilenir; art arda değişiklikte yalnız son cevap yazılır.
    const onizlemeNo = useRef(0);

    useEffect(() => {
        if (!ayar)
            return;

        const no = ++onizlemeNo.current;

        if (gunler.length == 0 || saatler.length == 0) {
            setSonrakiler([]);
            return;
        }

        YurticiMutabakatService.zamanOnizle({ aktif, gunler, saatler })
            .then(r => { if (no == onizlemeNo.current && r.isSuccess) setSonrakiler(r.data ?? []); })
            .catch(() => { });
    }, [gunler, saatler])

    const gunDegistir = (g: number) => setGunler(gunler.includes(g) ? gunler.filter(x => x != g) : [...gunler, g].sort((a, b) => a - b));

    const saatEkle = () => {
        const s = yeniSaat.trim().padStart(5, '0');

        if (!saatGecerli(s)) {
            toast.error('Saati SS:dd biçiminde girin (örn. 10:00).');
            return;
        }

        if (!saatler.includes(s))
            setSaatler([...saatler, s].sort());
    }

    const kaydet = async () => {
        if (aktif && (gunler.length == 0 || saatler.length == 0)) {
            toast.error('Otomatik çalışma için en az bir gün ve bir saat seçin.');
            return;
        }

        try {
            dispatch(storeActivityIndicator(true));

            var response = await YurticiMutabakatService.ayarKaydet({ aktif, gunler, saatler, kime, bilgi });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            yukle(response.data);
            toast.success('Ayarlar kaydedildi.');
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const ozet = () => {
        if (!aktif)
            return <span><b>Otomatik çalışma kapalı.</b> Mail kutusuna yalnız Gider Mutabakatı ekranındaki "Şimdi çalıştır" ile bakılır.</span>;

        if (gunler.length == 0 || saatler.length == 0)
            return <span className="text-danger"><b>Gün ve saat seçin.</b></span>;

        return (
            <span>
                <b>{sonrakiler[0] ? 'İlk çalışma ' + sonrakiler[0] + '.' : ''}</b>{' '}
                Her {GUNLER.filter(x => gunler.includes(x.value)).map(x => x.label).join(', ')} günü {saatler.join(', ')}'de Yurtiçi rapor mailine bakılır;
                yeni rapor varsa indirilip karşılaştırılır. Aynı rapor ikinci kez indirilmez.{' '}
                {kime ? <>Yeni fark ya da hata olursa <b>{kime}</b>{bilgi ? <> (bilgi: {bilgi})</> : null} adresine mail gider.</> : 'Bildirim alıcısı yok; sonuçlar yalnız ekranda görünür.'}
            </span>
        );
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={mutabakatYetkisi ? (
                <div className='m-0'>
                    <a onClick={() => navigate('/yurticimutabakat/list')} className='btn btn-sm fw-bold btn-light-primary'>Gider Mutabakatı</a>
                </div>
            ) : undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    {
                        ayar && !ayar.mailKutusuHazir && (
                            <div className="alert alert-light-warning mb-5">
                                Sunucuda Yandex mail kutusu ayarı eksik (kullanıcı ya da uygulama şifresi). Zamanlama kaydedilir ama mail okunamaz.
                            </div>
                        )
                    }

                    <KTCard className="ebbe-card">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Zamanlama</h4>

                            <div className="row">
                                <div className="col-md-2">
                                    <label className="form-label">Otomatik çalışma</label>
                                    <CustomSwitch isActive={aktif} setIsActive={setAktif as any} disabled={!guncelleyebilir} />
                                </div>

                                <div className="col-md-10">
                                    <label className="form-label required">Günler</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {
                                            GUNLER.map(g => (
                                                <button
                                                    key={g.value}
                                                    id={'mutabakat-gun-' + g.value}
                                                    type="button"
                                                    disabled={!guncelleyebilir}
                                                    className={'btn btn-sm ' + (gunler.includes(g.value) ? 'btn-primary' : 'btn-light')}
                                                    onClick={() => gunDegistir(g.value)}
                                                >
                                                    {g.label}
                                                </button>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-8">
                                <div className="col-md-6">
                                    <label className="form-label required" htmlFor="mutabakat-yeni-saat">Saatler</label>
                                    <div className="d-flex gap-2 align-items-center">
                                        <input
                                            id="mutabakat-yeni-saat"
                                            type="time"
                                            className="form-control form-control-sm w-125px"
                                            value={yeniSaat}
                                            disabled={!guncelleyebilir}
                                            onChange={e => setYeniSaat(e.target.value)}
                                            onKeyDown={e => { if (e.key == 'Enter') saatEkle(); }}
                                        />
                                        <button type="button" className="btn btn-sm btn-light-primary" disabled={!guncelleyebilir} onClick={saatEkle}>Saat ekle</button>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        {saatler.length == 0 && <span className="text-muted fs-7">Saat eklenmedi.</span>}
                                        {
                                            saatler.map(s => (
                                                <span key={s} className="badge badge-light-primary fs-6 py-2 px-3">
                                                    {s}
                                                    {guncelleyebilir && <i className="fa fa-times ms-2 cursor-pointer" title="Kaldır" onClick={() => setSaatler(saatler.filter(x => x != s))} />}
                                                </span>
                                            ))
                                        }
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Sonraki 5 çalışma</label>
                                    <div className="fs-7 text-gray-700" style={{ fontFamily: 'monospace' }}>
                                        {!aktif || sonrakiler.length == 0 ? <span className="text-muted">—</span> : sonrakiler.map(z => <div key={z}>{z}</div>)}
                                    </div>
                                    {ayar?.sonCalisma && <div className="text-muted fs-8 mt-2">Son otomatik çalışma: {ayar.sonCalisma}</div>}
                                    {
                                        ayar && ayar.tekrarDenemesi > 0 && (
                                            <div className="text-warning fs-7 fw-bold mt-2">
                                                Son çalışmada hata oldu; {ayar.sonrakiCalisma}'de kendiliğinden tekrar denenecek ({ayar.tekrarDenemesi}/{ayar.tekrarSiniri}).
                                            </div>
                                        )
                                    }
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-6">
                                Yurtiçi faturayı çarşamba kesiyor; rapor maili birkaç gün sonra gelebiliyor (23.10 raporu cuma 09:26'da geldi).
                                Birden çok gün seçmek sistemi yormaz: zamanı gelmeyen dakikalarda mail kutusuna gidilmez, aynı rapor ikinci kez indirilmez.
                                Mail kutusuna bağlanılamaz ya da rapor indirilemezse 30 dakika sonra kendiliğinden tekrar denenir (en fazla 3 kez), sonra normal zamanlamaya dönülür.
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Bildirim</h4>

                            <div className="row">
                                <div className="col-md-6">
                                    <MailListesiAlani labelText="Kime (yeni fark ve hata maili; virgülle birden çok)" value={kime} setValue={setKime} rows={2} />
                                </div>
                                <div className="col-md-6">
                                    <MailListesiAlani labelText="Bilgi / CC (virgülle birden çok)" value={bilgi} setValue={setBilgi} rows={2} />
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-4">
                                Mail yalnız yeni bir "Tutar farklı" ya da "Keylogx'te yok" kaydı çıktığında veya rapor alınamadığında gider (indirme hatasında ilk hatada ve deneme hakkı bitince). Kime boşsa mail atılmaz.
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <div className="p-4 rounded bg-light-primary fs-6 mb-5">{ozet()}</div>

                            {
                                ayar && (
                                    <div className="text-muted fs-8 mb-5">
                                        Mail kutusu: {ayar.mailKutusu ?? 'tanımlı değil'} · Sunucu zamanlayıcısı: {ayar.zamanlayiciAcik ? 'açık' : 'kapalı (bu ortamda otomatik çalışma yapılmaz)'}
                                    </div>
                                )
                            }

                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                {
                                    guncelleyebilir && (
                                        <Box>
                                            <button className="btn btn-primary btn-sm" onClick={kaydet}>Kaydet</button>
                                        </Box>
                                    )
                                }
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default YurticiMutabakatAyar;
